import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUp, BookOpen, ShieldCheck } from "lucide-react";
import Navbar from "../../../../../components/Landing_page_Navbar/index";
import TermsSidebar from "./TermsSidebar";
import TermsMobileNav from "./TermsMobileNav";
import TermsContent from "./TermsContent";
import { termsData, getTermById } from "../data/termsHelper";
import "../styles/generalTerms.css";

export default function GeneralTermsPage() {
  const location = useLocation();
  const contentTopRef = useRef(null);

  // Initialize active tab from URL hash or fallback to first section
  const [activeId, setActiveId] = useState(() => {
    const hash = window.location.hash.replace(/^#/, "");
    if (hash) {
      const match = getTermById(hash);
      if (match?.id) return match.id;
    }
    return termsData[0]?.id || "general-terms";
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Listen to hash changes (e.g., browser back/forward buttons)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace(/^#/, "");
      if (hash) {
        const found = getTermById(hash);
        if (found?.id && found.id !== activeId) {
          setActiveId(found.id);
        }
      }
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [activeId]);

  // Update hash when tab changes
  const handleSelectSection = (id) => {
    setActiveId(id);
    if (window.history.replaceState) {
      window.history.replaceState(null, "", `#${id}`);
    } else {
      window.location.hash = id;
    }

    // Smooth scroll to top of content area on mobile or when scrolled
    if (contentTopRef.current) {
      const navOffset = 80;
      const elementPosition =
        contentTopRef.current.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: Math.max(0, elementPosition - navOffset),
        behavior: "smooth",
      });
    }
  };

  // Scroll to top on first mount
  useEffect(() => {
    window.scrollTo(0, 0);

    const updateBackToTopVisibility = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    updateBackToTopVisibility();
    window.addEventListener("scroll", updateBackToTopVisibility, { passive: true });
    return () => window.removeEventListener("scroll", updateBackToTopVisibility);
  }, []);

  const activeSection = getTermById(activeId);

  return (
    <div className="terms-page-wrapper">
      {/* Platform Header / Navigation */}
      <Navbar />

      {/* Hero Banner */}
      <header className="terms-hero-section py-4 py-md-5">
        <div className="container-xl">
          <div className="row align-items-center">
            <div className="col-12 col-md-9 col-lg-8">
              <div className="terms-hero-badge mb-2">
                <ShieldCheck size={14} />
                <span>SELECTEZI Legal Documentation</span>
              </div>
              <h1 className="h2 fw-bold text-dark mb-2">
                Terms of Service &amp; Showcase Conditions
              </h1>
              <p className="text-secondary mb-0">
                Review the universal terms, compliance standards, and specialized
                showcase guidelines governing the SELECTEZI global business ecosystem.
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Sticky Selector */}
      <TermsMobileNav
        sections={termsData}
        activeSection={activeSection}
        onSelectSection={handleSelectSection}
      />

      {/* Main Two-Column Layout */}
      <main className="container-xl py-4 py-md-5 flex-grow-1" ref={contentTopRef}>
        <div className="row g-4 align-items-start">
          {/* Left Desktop Sidebar Navigation */}
          <div className="col-lg-4 col-xl-3">
            <TermsSidebar
              sections={termsData}
              activeId={activeId}
              onSelectSection={handleSelectSection}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
          </div>

          {/* Right Content Area */}
          <div className="col-12 col-lg-8 col-xl-9">
            <TermsContent
              activeSection={activeSection}
              sections={termsData}
              onSelectSection={handleSelectSection}
            />
          </div>
        </div>
      </main>

      {/* Standard Legal Footer */}
      <footer className="terms-footer p-3 mt-auto">
        <div className="container-xl d-flex align-items-center justify-content-between flex-wrap gap-2">
          <span>&copy; 2025 SELECTEZI Inc. All rights reserved.</span>

          <div className="d-flex align-items-center gap-3">
            <Link to="/general-terms" className="fw-semibold text-dark">
              General Terms
            </Link>
            <Link to="/privacy-policy">Privacy Policy</Link>
          </div>
        </div>
      </footer>

      {/* Floating Back to Top Button */}
      <button
        type="button"
        className={`terms-back-to-top ${
          showBackToTop ? "terms-back-to-top--visible" : ""
        }`}
        aria-label="Back to top"
        title="Back to top"
        tabIndex={showBackToTop ? 0 : -1}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        <ArrowUp size={20} strokeWidth={2.2} aria-hidden="true" />
      </button>
    </div>
  );
}

